import { cn } from '@/lib/utils';
import { LuvelleLogo } from './LuvelleLogo';

/**
 * Full Luvelle lockup — mark + serif wordmark + "HOME SPA" subline.
 *
 * Variants:
 *   - `stack`  (default) — logo above the wordmark; used on splash and
 *                          empty-state hero moments.
 *   - `inline` — logo to the start side of the wordmark; used in the
 *                top header and footers.
 *   - `mark`   — just the lotus+hands mark, no wordmark text. Useful on
 *                tight mobile headers where space is at a premium.
 *
 * Color is inherited from `currentColor`: set `text-primary` on a light
 * surface, `text-white` on a plum/gradient surface.
 *
 * Arabic locale: Latin "Luvelle" serif looks odd alongside Arabic UI,
 * so we render "لوفيل" in Almarai when `<html lang="ar">` and "Luvelle"
 * in Cormorant Garamond otherwise. The "HOME SPA" tagline stays Latin
 * on both for brand consistency (matches the brand guide letterhead).
 */
export function LuvelleWordmark({
  className,
  variant = 'stack',
  showTagline = true,
  size = 'md',
}: {
  className?: string;
  variant?: 'stack' | 'inline' | 'mark';
  showTagline?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const markSize = {
    sm: 'h-7 w-7',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
  }[size];

  const wordSize = {
    sm: 'text-xl',
    md: 'text-2xl md:text-3xl',
    lg: 'text-4xl md:text-5xl',
    xl: 'text-5xl md:text-7xl',
  }[size];

  const taglineSize = {
    sm: 'text-[8px] tracking-[0.3em]',
    md: 'text-[10px] tracking-[0.35em]',
    lg: 'text-xs tracking-[0.4em]',
    xl: 'text-sm tracking-[0.45em]',
  }[size];

  if (variant === 'mark') {
    return <LuvelleLogo className={cn(markSize, className)} />;
  }

  const word = (
    <div className="flex flex-col items-center leading-none">
      <span
        className={cn(
          'font-serif italic font-semibold leading-none',
          // Arabic locale renders Almarai "لوفيل"; Latin renders "Luvelle" serif.
          '[html[lang=ar]_&]:font-almarai [html[lang=ar]_&]:not-italic [html[lang=ar]_&]:font-extrabold',
          wordSize,
        )}
      >
        <span className="hidden [html[lang=ar]_&]:inline">لوفيل</span>
        <span className="hidden [html[lang=en]_&]:inline">Luvelle</span>
      </span>
      {showTagline && (
        <span className={cn('uppercase font-medium opacity-80 mt-1', taglineSize)}>
          Home Spa
        </span>
      )}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2.5 rtl:flex-row-reverse', className)}>
        <LuvelleLogo className={markSize} />
        {word}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <LuvelleLogo className={markSize} />
      {word}
    </div>
  );
}
